import type { ParsedUrlQuery } from 'querystring'
import type { GetServerSideProps } from 'next'

import { getWalk } from 'lib/content'
import createHref from 'lib/createHref'
import sanitizeKalturaUrl from 'lib/sanitizeKalturaUrl'
import { createSaveProgressParams } from 'lib/hooks/api/useSaveProgress'
import StopOverview, {
  StopOverviewProps,
} from 'components/presentation/StopOverview'
import { createStreetViewStopProps } from 'components/StreetView'
import { createNavBarProps } from 'components/NavBar'

const PageComponent = StopOverview

interface PageParams extends ParsedUrlQuery {
  walk: string
  stop: string
}

export const getServerSideProps: GetServerSideProps<
  StopOverviewProps,
  PageParams
> = async ({ params, locale = 'en' }) => {
  if (!params) {
    throw new Error('No parameters passed to getServerSideProps')
  }

  const walk = await getWalk(params.walk, locale)

  if (walk == null) {
    return { notFound: true }
  }

  const stopIndex = walk.stops.findIndex(stop => stop.slug === params.stop)
  const stop = stopIndex !== -1 ? walk.stops[stopIndex] : null

  if (stop == null) {
    return { notFound: true }
  }

  const firstSegmentSlug = stop.segments?.[0]?.slug ?? null

  const continueHref = firstSegmentSlug
    ? createHref({
        ...params,
        segment: firstSegmentSlug,
      })
    : null

  const mapHref = createHref({
    walk: params.walk,
    completedStop: stopIndex > 0 ? stopIndex : undefined,
  })

  return {
    props: {
      imageUrl: sanitizeKalturaUrl(stop.image_url),
      description: stop.description[locale] ?? '',
      title: stop.name[locale] ?? '',
      navBarProps: createNavBarProps(walk, params, locale),
      streetViewProps: createStreetViewStopProps(stop, locale),
      continueHref,
      mapHref,
      walkSlug: walk.slug,
      saveProgressParams: createSaveProgressParams(walk, stop),
    },
  }
}

export default PageComponent
